import React, { useState, useEffect, useRef } from 'react';
import { ticketService } from '@services/api/ticket.service';
import { aiHelpService } from '@services/api/ai-help.service';
import type { TicketCategory, TicketPriority } from '@/types/ticket';

interface SupportTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  previousMessage?: string;
  aiSessionId?: string;
}

interface TicketFormData {
  title: string;
  categoryId: number | '';
  priority: TicketPriority;
  description: string;
  errorDetails: string;
}

const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;

const EMPTY_FORM: TicketFormData = { title: '', categoryId: '', priority: 'medium', description: '', errorDetails: '' };

export const SupportTicketModal: React.FC<SupportTicketModalProps> = ({
  isOpen,
  onClose,
  previousMessage = '',
  aiSessionId,
}) => {
  const [categories, setCategories] = useState<TicketCategory[]>([]);
  const [formData, setFormData] = useState<TicketFormData>({ ...EMPTY_FORM, description: previousMessage });
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [attachmentError, setAttachmentError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [ticketNo, setTicketNo] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    setFormData((prev) => ({ ...prev, description: previousMessage || prev.description }));
    ticketService.listCategories().then(setCategories).catch(() => setCategories([]));
  }, [isOpen, previousMessage]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'categoryId' ? (value ? Number(value) : '') : value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setAttachmentError('');
    if (file && file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      setAttachmentError('File must be 10MB or smaller.');
      setAttachmentFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setAttachmentFile(file);
  };

  const removeAttachment = () => {
    setAttachmentFile(null);
    setAttachmentError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      let attachmentFields: {
        attachment_url?: string; attachment_name?: string;
        attachment_mime_type?: string; attachment_size?: number;
      } = {};
      if (attachmentFile) {
        const uploaded = await ticketService.uploadAttachment(attachmentFile);
        attachmentFields = {
          attachment_url: uploaded.url,
          attachment_name: uploaded.file_name,
          attachment_mime_type: uploaded.mime_type || undefined,
          attachment_size: uploaded.file_size || undefined,
        };
      }

      let ticket;
      if (aiSessionId) {
        ticket = await aiHelpService.escalate({
          session_id: aiSessionId,
          title: formData.title,
          category_id: formData.categoryId === '' ? undefined : formData.categoryId,
          priority: formData.priority,
          error_details: formData.errorDetails || undefined,
          ...attachmentFields,
        });
      } else {
        ticket = await ticketService.create({
          title: formData.title,
          description: formData.description,
          error_details: formData.errorDetails || undefined,
          category_id: formData.categoryId === '' ? undefined : formData.categoryId,
          priority: formData.priority,
          source: 'help_support',
          ...attachmentFields,
        });
      }

      setTicketNo(ticket.ticket_no);
      setSubmitSuccess(true);
      // The modal is opened from several places (sidebar, help bot, tickets page);
      // let any mounted ticket list refresh itself.
      window.dispatchEvent(new CustomEvent('ticket:created', { detail: ticket }));

      setTimeout(() => {
        setFormData({ ...EMPTY_FORM });
        removeAttachment();
        setSubmitSuccess(false);
        onClose();
      }, 2000);
    } catch (error: any) {
      setErrorMessage(error?.userMessage || error?.message || 'Failed to create ticket. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-lg shadow-2xl border border-gray-200 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white rounded-t-lg z-10">
          <div className="flex items-center gap-3">
            <span className="text-xl">🎫</span>
            <div>
              <h2 className="font-bold text-lg text-gray-900">Create Support Ticket</h2>
              <p className="text-xs text-gray-500">Our team will respond shortly</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-base p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        {submitSuccess ? (
          <div className="p-8 flex flex-col items-center justify-center gap-4 min-h-[400px]">
            <div className="text-6xl animate-bounce">✅</div>
            <h3 className="text-xl font-bold text-gray-900">Ticket Created Successfully!</h3>
            <p className="text-sm text-gray-500 text-center">
              Your support ticket has been submitted. You'll receive updates via notifications.
            </p>
            <div className="mt-4 p-3 rounded-lg bg-indigo-50 border border-indigo-100 w-full text-center">
              <p className="text-sm font-semibold text-indigo-700">
                Ticket No: {ticketNo}
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {errorMessage && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{errorMessage}</div>
            )}

            {/* Title */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Subject *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Brief title of your issue"
                required
                minLength={3}
                className="text-sm w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
              />
            </div>

            {/* Category + Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Category
                </label>
                <select
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={handleInputChange}
                  className="text-sm w-full px-4 py-2 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                >
                  <option value="">Select a category</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Priority
                </label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  className="text-sm w-full px-4 py-2 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                >
                  <option value="low">Low - Can wait</option>
                  <option value="medium">Medium - Normal</option>
                  <option value="high">High - Urgent</option>
                  <option value="critical">Critical - Blocking</option>
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Please describe your issue in detail..."
                required
                minLength={10}
                rows={5}
                disabled={!!aiSessionId}
                className="text-sm w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 resize-none disabled:bg-gray-50 disabled:text-gray-500"
              />
              {aiSessionId && (
                <p className="text-xs mt-1 text-gray-400">
                  Prefilled from your AI chat conversation, which will be linked to this ticket.
                </p>
              )}
            </div>

            {/* Error Details / Exception */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Error Details / Exception <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <textarea
                name="errorDetails"
                value={formData.errorDetails}
                onChange={handleInputChange}
                placeholder="Paste any error message, exception, or stack trace here, if applicable..."
                rows={3}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 resize-none"
              />
            </div>

            {/* Attachment */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Attachment <span className="font-normal text-gray-400">(optional, max 10MB)</span>
              </label>
              {attachmentFile ? (
                <div className="flex items-center justify-between px-4 py-2 rounded-lg border border-gray-300 bg-gray-50">
                  <span className="text-sm text-gray-700 truncate">📎 {attachmentFile.name}</span>
                  <button
                    type="button"
                    onClick={removeAttachment}
                    className="text-xs text-red-600 hover:text-red-700 font-semibold ml-3"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileChange}
                  className="w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-indigo-50 file:text-indigo-700 file:font-semibold hover:file:bg-indigo-100"
                />
              )}
              {attachmentError && <p className="text-xs mt-1 text-red-600">{attachmentError}</p>}
            </div>

            {/* Info Box */}
            <div className="p-4 rounded-lg bg-indigo-50 border border-indigo-100">
              <p className="text-sm text-gray-700">
                💡 <span className="font-semibold">Pro tip:</span> Be as specific as possible to help our team resolve your issue faster.
              </p>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={onClose}
                className="text-sm flex-1 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors font-semibold"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !formData.title.trim() || !formData.description.trim()}
                className="text-sm flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50 hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <span className="animate-spin">⚙️</span>
                    Creating...
                  </>
                ) : (
                  <>
                    <span>🎫</span>
                    Create Ticket
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
